import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Stores the session once the second factor has been satisfied.
  const completeAuth = (data) => {
    if (data?.token) localStorage.setItem("yk_token", data.token);
    setUser(data);
    return data;
  };

  // Returns either a full session or an MFA challenge
  // ({ mfa_required | mfa_setup_required, mfa_token }).
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required || data.mfa_setup_required) return data;
    return completeAuth(data);
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.mfa_required || data.mfa_setup_required) return data;
    return completeAuth(data);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("yk_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser, completeAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
};
